import { useState } from "react";

import HistoryIcon from "@mui/icons-material/History";
import KPIIcon from "@mui/icons-material/Info";
import AddEntryIcon from "@mui/icons-material/PlaylistAdd";
import { Divider, Typography } from "@mui/material";
import { Tab, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { deepCopyObject, helperGetIsoDate } from "../../utils/helpers";
import KPIGraph from "../graphing/kpiGraph";
import EmpireKPIValueListItem from "../ui/empireKPIValueListItem";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogList from "../ui/omniDialogList";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniEditButton from "../ui/omniEditButton";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniTextField from "../ui/omniTextField";
import TextInputDialog from "./textInputDialog";

const statistical = require("statistical-js");

const EmpireKPIDialog = props => {
  const {
    open,
    dialogTitle = "Create API",
    okMessage = "OK",
    cancelMessage = "Cancel",
    maxWidth = "sm",
    fullWidth = true,
    omniODM, //omni Data Model Object, a root level object representation, Pass in a COPY, the only way this works

    tabId = 0,
  } = props;

  // const [readyOk, setReadyOk] = useState(false);
  const [editedODM, setEditedODM] = useState(deepCopyObject(omniODM));
  const [valueEntry, setShowValueEntry] = useState(null);

  const updateEditedODM = event => {
    try {
      editedODM.omniData[event.target.id] = event.target.value;
      setEditedODM(deepCopyObject(editedODM));
    } catch (error) {
      console.log(error);
    }
  };

  const [tabIndex, setTabIndex] = useState(tabId);

  const onAcceptHandler = () => {
    //notice we didn't touch the id

    //add the new data to the value
    props.onAccept(editedODM);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const theme = useTheme();

  const smallScreen = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const getLatestValueString = kpiODM => {
    let theReturn = `Latest Value: ${kpiODM.omniData.preUnitSymbol}`;
    theReturn += kpiODM.omniData.values[kpiODM.omniData.values.length - 1].kpiValue;
    theReturn += " " + kpiODM.omniData.postUnitLabel;
    return theReturn;
  };

  const getAverageValueString = kpiODM => {
    let theReturn = `Average Value: ${kpiODM.omniData.preUnitSymbol}`;
    let average = 0;
    for (let index = 0; index < kpiODM.omniData.values.length; ++index) {
      average += parseInt(kpiODM.omniData.values[index].kpiValue);
    }
    average = average / kpiODM.omniData.values.length;
    theReturn += Math.round(average);
    theReturn += " " + kpiODM.omniData.postUnitLabel;
    return theReturn;
  };

  const getLinearRegressionString = kpiODM => {
    let theReturn = `Linear Regression: `;
    let linearInput = [];
    for (let index = 0; index < kpiODM.omniData.values.length; ++index) {
      linearInput.push([index, parseInt(kpiODM.omniData.values[index].kpiValue)]);
    }
    //this takes an array [x,y] values
    let linearOutput = statistical.methods.linearRegression(linearInput);

    const toFixed = linearOutput.slope.toFixed(2);
    if (toFixed > 0.01) {
      theReturn += "Increasing";
    } else if (toFixed < -0.01) {
      theReturn += "Decreasing";
    } else {
      theReturn += "Steady";
    }
    theReturn += ` (${toFixed})`;

    return theReturn;
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogTabs tabIndex={tabIndex} setTabIndex={setTabIndex}>
        <Tab key={`tab0`} icon={<KPIIcon />} label={`Details`} />
        <Tab
          key={`tab1`}
          icon={<HistoryIcon />}
          label={`${editedODM.omniData.values.length} Week History`}
        />
        {/* <Tab key={`tab2`} icon={<GrpahIcon />} label={`Visualization`} /> */}
      </DialogTabs>
      <DialogContent
        sx={[
          !smallScreen && { minHeight: "326px", maxHeight: "326px" },
          tabIndex === 3 && { padding: 0 },
        ]}
      >
        {/* USER INFORMATION TAB */}
        <DialogTab selectedIndex={tabIndex} index={0}>
          <Typography
            variant="overline"
            sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
          >
            {`KPI Details`}
          </Typography>

          <Grid container>
            <Grid container item spacing={1}>
              <Grid item xs={6}>
                <OmniTextField
                  label="Business Pillar"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"businessPillar"}
                  key={"businessPillar"}
                  value={editedODM.omniData.businessPillar}
                  select
                  onChange={event => {
                    event.target["id"] = "businessPillar";
                    updateEditedODM(event);
                  }}
                >
                  {constants.empirePillarTypes.map(empirePillar => (
                    <OmniMenuItem key={empirePillar.value} value={empirePillar.value}>
                      {empirePillar.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>

              <Grid item xs={6}>
                <OmniTextField
                  label="Name"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"name"}
                  key={"name"}
                  value={editedODM.omniData.name}
                  onChange={event => {
                    updateEditedODM(event);
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <OmniTextField
                  label="Red Light Value"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"underRangeValue"}
                  key={"UnunderRangeValueder"}
                  value={editedODM.omniData.underRangeValue}
                  onChange={event => {
                    updateEditedODM(event);
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <OmniTextField
                  label="Target Value"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"targetValue"}
                  key={"targetValue"}
                  value={editedODM.omniData.targetValue}
                  onChange={event => {
                    updateEditedODM(event);
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <OmniTextField
                  label="Green Light Value"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"overRangeValue"}
                  key={"overRangeValue"}
                  value={editedODM.omniData.overRangeValue}
                  onChange={event => {
                    updateEditedODM(event);
                  }}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={1}>
              <Grid item xs={12}>
                <div sx={{ minHeight: 0, maxHeight: 0 }} />
              </Grid>
            </Grid>

            {editedODM.omniData.values.length > 0 && (
              <Grid container item>
                <Typography
                  variant="overline"
                  sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
                >
                  Analytics
                </Typography>
                <Grid container item spacing={1}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "left",
                        paddingLeft: theme.spacing(1),
                      }}
                    >
                      <Typography variant="h6">{getLatestValueString(editedODM)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "left",
                        paddingLeft: theme.spacing(1),
                      }}
                    >
                      <Typography variant="h6">{getAverageValueString(editedODM)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "left",
                        paddingLeft: theme.spacing(1),
                      }}
                    >
                      <Typography variant="h6">{getLinearRegressionString(editedODM)}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogTab>
        {/* USER ASSESSMENTS TAB */}
        <DialogTab selectedIndex={tabIndex} index={1}>
          <Grid container>
            <Grid item xs={10} sm={11}>
              <Typography
                variant="h6"
                sx={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}
              >
                {`${editedODM.omniData.values.length} entries`}
              </Typography>
            </Grid>
            <Grid item xs={2} sm={1}>
              <Box sx={{ position: "relative" }}>
                <OmniEditButton
                  sx={{ top: 8 }}
                  title={"Add a value"}
                  onClick={e => {
                    e.stopPropagation();
                    setShowValueEntry({
                      dateOfEntry: helperGetIsoDate(),
                      kpiValue: 0,
                    });
                  }}
                  icon={<AddEntryIcon size="large" />}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid container>
              <DialogList sx={{ padding: theme.spacing(1), width: "100%" }}>
                {editedODM.omniData.values.toReversed().map((valueODM, index) => (
                  <EmpireKPIValueListItem
                    kpiValueODM={valueODM}
                    index={editedODM.omniData.values.length - index}
                    upper={editedODM.omniData.overRangeValue}
                    lower={editedODM.omniData.underRangeValue}
                    kpiName={editedODM.omniData.name}
                  />
                ))}
              </DialogList>
            </Grid>
          </Grid>
        </DialogTab>
        {/* Graphics*/}
        <DialogTab selectedIndex={tabIndex} index={2}>
          <Grid container>
            <Grid item xs={12}>
              <KPIGraph
                sx={{ marginLeft: theme.spacing(2), margingRight: theme.spacing(2) }}
                // graphTitle={singleSymptomData.graphTitle}
                // rawGraphData={singleSymptomData.graphDataComplete}
                // graphLabels={singleSymptomData.graphLabels}
                // graphIndex={singleSymptomData.graphIndex}
                // graphTimeRange={graphTimeRange}
                // graphStyle={graphStyle}
                // averageSeverity={singleSymptomData.averageSeverity}
              />
            </Grid>
          </Grid>
        </DialogTab>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}></Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                // disabled={!readyOk}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {valueEntry && (
        <TextInputDialog
          inputLabel={`${"KPI value"}`}
          open={valueEntry}
          dialogTitle={`KPI Entry`}
          dialogMessage={"Please enter the KPI value for this week"}
          onAccept={theValue => {
            try {
              let newODM = deepCopyObject(editedODM);
              theValue = theValue.replace(/[^0-9]/g, "");
              valueEntry.kpiValue = theValue;

              newODM.omniData.values.push(valueEntry);
              setEditedODM(deepCopyObject(newODM));
            } catch (error) {
              console.log(error);
            }
            setShowValueEntry(null);
          }}
          onClose={() => {
            setShowValueEntry(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default EmpireKPIDialog;
